import { ActivatedRoute, Data, ParamMap, Params } from "@angular/router";
import { BehaviorSubject, combineLatest, merge, Observable, of } from "rxjs";
import { map, switchMap } from "rxjs/operators";
import { ShellService } from "src/app/shell/shell.service";
import { IDataPanelViewModel, IRowViewModel, IViewData } from "../entities/view-model";
import { IFormCommentAcceptedEventArg } from "../forms/form-comment/form-comment.component";
import { FormOkCancelComponent, IFormOkCancelOptions } from "../forms/form-ok-cancel/form-ok-cancel.component";
import { IPageContentFormViewModel, IPageContentViewModel, IPageDataViewModel, IPageRouteContext, IPageViewModel } from "./pages";

export class BasePageComponent {

  constructor(protected shellService: ShellService = undefined, protected activatedRoute: ActivatedRoute = undefined) { }

  private refresher: BehaviorSubject<IPageRouteContext> = new BehaviorSubject(undefined);

  pageVm$: Observable<IPageViewModel>;

  /**
   *
   * start a form, disable actions, hide content
   *
   * @param pageVm
   * @param formVm
   */
  formBegin<typeOfForm extends IPageContentFormViewModel>(pageVm: IPageViewModel, formVm: typeOfForm): typeOfForm {
    pageVm.contentVm.formVm = formVm;
    pageVm.actionsAllowed = false;

    return formVm;
  }

  /**
   *
   * @param formVm
   * @param loadFunc
   */
  async formLoadAsync(formVm: IPageContentFormViewModel, loadFunc: () => Observable<any>) {
    try {
      formVm.loading = true;

      await loadFunc().toPromise();

    } catch (err) {
      formVm.loadingErrorText = err;
    } finally {
      formVm.loading = false;
    }
  }

  /**
   * end the form without reloading the content
   *
   * @param pageVm
   */
  formEnd(pageVm: IPageViewModel) {
    pageVm.actionsAllowed = true;
    pageVm.contentVm.formVm = undefined;
  }

  /**
   * end the form and reload the content
   *
   * @param pageVm
   */
  formEndRefresh(pageVm: IPageViewModel) {
    this.formEnd(pageVm);
    this.refresher.next(pageVm.routeContext);
  }

  /**
   *
   * @param type
   * @param options
   * @returns
   */
  formVm(type: string, options?: { okCancelOptions?: IFormOkCancelOptions }): IPageContentFormViewModel {
    const ret: IPageContentFormViewModel = {
      type: type,

      okCancelVm: options?.okCancelOptions ? FormOkCancelComponent.okCancelVm(options.okCancelOptions) : undefined
    };
    return ret;
  }

  /**
   * @param pvm
   * @param vm
   */
  protected onPageAddNew(pvm: IPageViewModel, vm: IPageContentViewModel) {
    console.log('page add new');
  }

  /**
   *
   * @returns
   */
  protected routeContext(): Observable<IPageRouteContext> {
    const ret = combineLatest([
      this.activatedRoute?.params ?? of({}),
      this.activatedRoute?.queryParams ?? of({}),
      this.activatedRoute?.data ?? of({})
    ]).pipe(
      map(([params, queryParams, data]) => ({ params: params, queryParams: queryParams, data: data })),
    );
    return ret;
  }

  /**
   * {
   * @param loaderFunc
   */
  protected onInit(loaderFunc: (arg: IBasePageOnInitArg) => Observable<IPageViewModel>, options?: { canGoBack?: boolean, canRefresh?: boolean, canAddNew?: boolean }) {
    const arg: IBasePageOnInitArg = {};

    const init$ = of<IPageViewModel>({ workingText: 'Loading...' });

    this.pageVm$ = combineLatest([
      this.refresher, this.routeContext()
    ]).pipe(
      map(([r, r2]) => r || r2),

      map((ctx: IPageRouteContext) => {
        const ret: IBasePageOnInitArg = {
          routeInfo: { ...ctx },
          oneTimeInfo: !this.shellService.isOneTime() ? undefined : {
            workflowItemStatus: ctx.params['wfis'],
            token: ctx.params['accessToken']
          }
        };
        return ret;
      }),

      switchMap((arg: IBasePageOnInitArg) => merge(init$, loaderFunc(arg))),

      map((r: IPageViewModel) => {
        return {
          ...r, ...options
        };
      }),
      map((r: IPageViewModel) => {
        if (r.notAuthenticated === true) {
          this.shellService.logout();
          return {

          };
        }

        return {
          ...r,

          refresh: r.canRefresh ? () => this.refresher.next(r.routeContext) : undefined,
          addNew: r.canAddNew ? (pvm, vm) => this.onPageAddNew(pvm, vm) : undefined
        };
      })
    );

  }

  //#region workflow form handling

  /**
   *
   * @param pvm
   * @param actionCode
   * @param row
   */
  protected workflowCompleteFormBegin(pvm: IPageViewModel, actionCode: string, row: IRowViewModel = undefined) {
    const title = actionCode == 'void' ? 'Void'
      : actionCode == 'approve' ? 'Approve'
        : actionCode == 'reject' ? 'Reject'
          : actionCode == 'ack' ? 'Acknowledge'
            : 'Unknown';

    const formVm: IWorkflowCompleteFormViewModel = {
      ...this.formVm(`workflow-complete`),
      title: title,
      actionCode: actionCode,
      row: row
    };
    this.formBegin(pvm, formVm);
  }

  /**
  * invoke the workflow action
  * @param fvm
  * @param comment
  * @returns
  */
  protected workflowFormCompleteActionInvoke(fvm: IWorkflowCompleteFormViewModel, comment: string = undefined): Observable<IPageDataViewModel> {
    const ret: IPageDataViewModel = {
      data: { actionCode: fvm.actionCode, entityCode: fvm.row.code, comment: comment }
    };
    return of(ret);
  }

  /**
   *
   * @param pvm
   * @param vm
   * @param fvm
   */
  async workflowCompleteFormOk(pvm: IPageViewModel, fvm: IWorkflowCompleteFormViewModel, arg: IFormCommentAcceptedEventArg) {
    const workingText = fvm.actionCode == 'void' ? 'Voiding...'
      : fvm.actionCode == 'approve' ? 'Approving...'
        : fvm.actionCode == 'reject' ? 'Rejecting...'
          : fvm.actionCode == 'ack' ? 'Acknowledging... '
            : 'Working...';

    const success = await FormOkCancelComponent.processOkAsync(arg.okCancelVm, workingText, () => this.workflowFormCompleteActionInvoke(fvm, arg.comment));
    if (success)
      this.formEndRefresh(pvm);
  }

  //#endregion workflow form handling

  /**
   *
   * @param panelData
   * @returns
   */
  protected dataPanelRowVms(panelData: IViewData): IRowViewModel[] {
    const rows: IRowViewModel[] = panelData.rows.map((r, ndx) => {
      const actionType = panelData.actionTypes?.length > ndx ? panelData.actionTypes[ndx] : undefined;

      const ret: IRowViewModel = {
        code: actionType?.entityCode,
        actions: actionType?.actions,
        fields: r
      };
      return ret;
    });
    return rows;
  }

  /**
   *
   * @param panelDatas
   * @returns
   */
  protected dataPanelTabVms(panelDatas: IViewData[]): IDataPanelTabViewModel[] {
    const ret = panelDatas.map(gp => {
      const rows: IRowViewModel[] = this.dataPanelRowVms(gp);
      const ret: IDataPanelTabViewModel = {
        title: gp.title,
        dataPanelVm: { data: gp, rows: rows, rowsFiltered: rows }
      };
      return ret;
    });
    return ret;
  }
}

/**
 *
 */
export interface IBasePageOnInitArg {
  routeInfo?: IPageRouteContext;

  oneTimeInfo?: {
    workflowItemStatus: string;
    token: string;
  }
}

/**
 *
 */
export interface IWorkflowCompleteFormViewModel extends IPageContentFormViewModel {
  actionCode: string;
  row: IRowViewModel;
}

/**
 *
 */
export interface IDataPanelTabViewModel {
  title: string;
  dataPanelVm: IDataPanelViewModel;
}
